import React, { useCallback, useMemo, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";

import { useApiUrl, useDataProvider } from "@refinedev/core";
import {
  UploadTaskSnapshot,
  getBlob,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import {
  AlertCircle,
  CheckCircle2,
  Crop,
  Loader2,
  Play,
  Upload,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { storage } from "@/firebaseConfig";
import { generateImageBlurhash } from "@/lib/imgUtils";
import { cn } from "@/lib/utils";
import { getVideoDimensions, processCropArea } from "@/lib/videoUtils";

export interface UploadedVideo {
  id: string;
  file: File;
  preview: string;
  status:
    | "pending"
    | "cropping"
    | "uploading"
    | "transcoding"
    | "generating-thumbnail"
    | "complete"
    | "error";
  progress: number;
  progressText?: string;
  publicUrl?: string;
  previewUrl?: string;
  thumbnailUrl?: string;
  thumbnailBlurhash?: string;
  manifestUrl?: string;
  cropArea?: {
    xl: number;
    xr: number;
    yt: number;
    yb: number;
  };
  hasAudio?: boolean;
  error?: string;
}

export interface CompletedVideoUpload {
  id: string;
  fileName: string;
  manifestUrl: string;
  previewUrl: string;
  thumbnailUrl: string;
  thumbnailBlurhash: string;
}

interface MultiVideoUploaderProps {
  onUploadsComplete?: (uploads: CompletedVideoUpload[]) => void;
  getPath: (file: File) => string;
  maxFiles?: number;
}

// Video cropper dialog component
function VideoCropperDialog({
  isOpen,
  file,
  onClose,
  onCropComplete,
}: {
  isOpen: boolean;
  file?: File;
  onClose: () => void;
  onCropComplete: (area: UploadedVideo["cropArea"], hasAudio: boolean) => void;
}) {
  const preview = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  const [area, setArea] = useState<Area>();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const aspect = 9 / 16; // Vertical video aspect ratio

  if (!preview) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Crop Video</DialogTitle>
          <DialogDescription>
            Adjust the crop area for your teaser video. Videos will be cropped
            to 9:16 aspect ratio.
          </DialogDescription>
        </DialogHeader>
        <div className="relative flex-1 min-h-0 bg-muted rounded-lg overflow-hidden">
          <Cropper
            video={preview}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, croppedArea) => {
              setArea(croppedArea);
            }}
          />
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (area) {
                const dimensions = await getVideoDimensions(file!);
                const { xl, xr, yt, yb } = processCropArea(area, dimensions);
                onCropComplete({ xl, xr, yt, yb }, dimensions.hasAudio);
              }
              onClose();
            }}
            disabled={!area}
          >
            <Crop className="mr-2 h-4 w-4" />
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Resumable upload with progress
async function uploadVideo(
  file: File,
  getPath: MultiVideoUploaderProps["getPath"],
  onProgress: (progress: number) => void,
  onError: (message: string) => void,
): Promise<UploadTaskSnapshot> {
  return new Promise<UploadTaskSnapshot>((resolve) => {
    const teasersRef = ref(storage, getPath(file));
    const uploadTask = uploadBytesResumable(teasersRef, file);
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(Math.round(progress));
      },
      (error) => {
        console.error(error);
        onError(error.message);
      },
      async () => {
        resolve(uploadTask.snapshot);
        // await transcode(
        //   `gs://${uploadTask.snapshot.ref.bucket}/${uploadTask.snapshot.ref.fullPath}`,
        //   uploadTask.snapshot.ref.fullPath,
        //   { xl, xr, yt, yb },
        //   dimensions.hasAudio,
        // );
      },
    );
  });
}

// Delay helper
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function MultiVideoUploader({
  onUploadsComplete,
  maxFiles = 10,
  getPath,
}: MultiVideoUploaderProps) {
  // refine related
  const dataProvider = useDataProvider();
  const apiUrl = useApiUrl();
  // state
  const [videos, setVideos] = useState<UploadedVideo[]>([]);
  const [completedUploads, setCompletedUploads] = useState<
    CompletedVideoUpload[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cropTarget, setCropTarget] = useState<UploadedVideo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const transcodeVideo = React.useCallback(
    async (
      inputUri: string,
      inputRelativePath: string,
      hasAudio: boolean,
      crop: { xl: number; xr: number; yt: number; yb: number },
      onError: (message: string) => void,
      onProgress: (progress: number) => void,
      onProgressText: (text: string) => void,
    ) => {
      const { custom } = dataProvider();

      const outputUri = `${inputUri}-transcoded/`;
      const outputRelativePath = `${inputRelativePath}-transcoded/`;

      try {
        let response = await custom?.({
          url: `${apiUrl}/media`,
          method: "post",
          payload: {
            inputUri: inputUri,
            outputUri: outputUri,
            hasAudio: hasAudio,
            crop: crop,
          },
        });
        if (!response?.data.name) {
          onError("Failed to create transcoder job");
          return;
        }

        let result = await custom?.({
          url: `${apiUrl}/media`,
          method: "get",
          query: {
            name: response?.data.name,
          },
        });

        let cnt = 0;
        while (result?.data.state !== 3 && result?.data.state !== 4) {
          cnt += 1;
          result = await custom?.({
            url: `${apiUrl}/media`,
            method: "get",
            query: {
              name: response?.data.name,
            },
          });

          switch (result?.data?.state) {
            case 1:
              onProgressText("Pending...");
              break;
            case 2:
              onProgressText("Transcoding...");
              onProgress(cnt * 10);
              break;
            case 3:
              onProgressText("Success");
              onProgress(100);
              break;
            case 4:
              onProgressText("Failed to transcode");
              onError(result?.data?.error?.message || "Error transcoding");
              break;
          }
          await delay(5000);
        }
      } catch (ex) {
        onError("Failed to transcode");
        throw ex;
      }

      return outputRelativePath;
    },
    [dataProvider],
  );

  const updateVideo = useCallback(
    (id: string, updates: Partial<UploadedVideo>) => {
      setVideos((prev) =>
        prev.map((vid) => (vid.id === id ? { ...vid, ...updates } : vid)),
      );
    },
    [],
  );

  const addCompletedUpload = useCallback((upload: CompletedVideoUpload) => {
    setCompletedUploads((prev) => [...prev, upload]);
  }, []);

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const videoFiles = fileArray.filter((f) => f.type.startsWith("video/"));

      if (videoFiles.length !== fileArray.length) {
        alert("Only video files are allowed. Some files were skipped.");
      }

      if (videos.length + videoFiles.length > maxFiles) {
        alert(`Maximum ${maxFiles} files allowed.`);
        return;
      }

      const newVideos: UploadedVideo[] = videoFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        status: "pending",
        progress: 0,
      }));

      setVideos((prev) => [...prev, ...newVideos]);
    },
    [videos.length, maxFiles],
  );

  const removeVideo = useCallback((id: string) => {
    setVideos((prev) => {
      const vid = prev.find((v) => v.id === id);
      if (vid) {
        URL.revokeObjectURL(vid.preview);
      }
      return prev.filter((v) => v.id !== id);
    });
  }, []);

  const openCropper = useCallback((video: UploadedVideo) => {
    setCropTarget(video);
  }, []);

  const handleCropComplete = useCallback(
    (area: UploadedVideo["cropArea"], hasAudio: boolean) => {
      if (cropTarget) {
        updateVideo(cropTarget.id, { cropArea: area, hasAudio });
      }
      setCropTarget(null);
    },
    [cropTarget, updateVideo],
  );

  const processVideo = async (video: UploadedVideo) => {
    try {
      if (!video.cropArea || video.hasAudio === undefined) {
        const dimensions = await getVideoDimensions(video.file);
        updateVideo(video.id, {
          hasAudio: dimensions.hasAudio,
          cropArea: { xl: 0, xr: 0, yb: 0, yt: 0 },
        });
      }

      // Upload video
      updateVideo(video.id, {
        status: "uploading",
        progress: 0,
        progressText: "Uploading...",
      });

      // Upload with progress
      const snapshot = await uploadVideo(
        video.file,
        getPath,
        (progress) => updateVideo(video.id, { progress }),
        (errorΜessage) =>
          updateVideo(video.id, { status: "error", error: errorΜessage }),
      );

      // Start transcoding
      updateVideo(video.id, {
        status: "transcoding",
        progress: 0,
        progressText: "Starting transcode...",
      });

      const outputRelativePath = await transcodeVideo(
        `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`,
        snapshot.ref.fullPath,
        video.hasAudio!,
        video.cropArea!,
        (error) => updateVideo(video.id, { status: "error", error: error }),
        (progress) => updateVideo(video.id, { progress: progress }),
        (progressText) => updateVideo(video.id, { progressText }),
      );

      // Generate thumbnail blurhash
      updateVideo(video.id, {
        status: "generating-thumbnail",
        progressText: "Generating thumbnail...",
      });

      const cdnUri = "https://musicofourdesire.b-cdn.net";
      const manifestUrl = `${cdnUri}/${outputRelativePath}manifest.m3u8`;
      const previewUrl = `${cdnUri}/${outputRelativePath}sd.mp4`;
      const thumbnailUrl = `${cdnUri}/${outputRelativePath}thumbnail0000000000.jpeg`;

      const blob = await getBlob(
        ref(storage, `${outputRelativePath}thumbnail0000000000.jpeg`),
      );
      const thumbnailBlurhash = await generateImageBlurhash(
        URL.createObjectURL(blob),
      );

      // Complete
      updateVideo(video.id, {
        status: "complete",
        manifestUrl,
        previewUrl,
        thumbnailUrl,
        thumbnailBlurhash,
        progressText: "Complete",
      });

      const completedUpload: CompletedVideoUpload = {
        id: video.id,
        fileName: video.file.name,
        manifestUrl,
        previewUrl,
        thumbnailUrl,
        thumbnailBlurhash,
      };

      addCompletedUpload(completedUpload);
      return completedUpload;
    } catch (error) {
      updateVideo(video.id, {
        status: "error",
        error: error instanceof Error ? error.message : "Upload failed",
        progressText: "Failed",
      });
      return null;
    }
  };

  const startUpload = async () => {
    if (videos.length === 0) return;

    setIsUploading(true);
    setCompletedUploads([]);

    const pendingVideos = videos.filter(
      (vid) => vid.status === "pending" || vid.status === "error",
    );

    // Process videos sequentially to avoid overloading
    const results: CompletedVideoUpload[] = [];
    for (const video of pendingVideos) {
      const result = await processVideo(video);
      if (result) {
        results.push(result);
      }
    }

    if (onUploadsComplete && results.length > 0) {
      onUploadsComplete(results);
    }

    setIsUploading(false);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const getStatusIcon = (status: UploadedVideo["status"]) => {
    switch (status) {
      case "cropping":
      case "uploading":
      case "transcoding":
      case "generating-thumbnail":
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case "complete":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Play className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusText = (video: UploadedVideo) => {
    if (video.progressText) return video.progressText;
    switch (video.status) {
      case "cropping":
        return "Cropping...";
      case "uploading":
        return "Uploading...";
      case "transcoding":
        return "Transcoding...";
      case "generating-thumbnail":
        return "Generating thumbnail...";
      case "complete":
        return "Complete";
      case "error":
        return "Error";
      default:
        return video.cropArea ? "Ready" : "Crop to adjust";
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer",
          "hover:border-primary/50 hover:bg-muted/50",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25",
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          multiple
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className="hidden"
        />
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <div className="p-3 rounded-full bg-muted">
            <Upload className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">
              Drop teaser videos here or click to browse
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Videos will be cropped to 9:16 and transcoded (max {maxFiles}{" "}
              files)
            </p>
          </div>
        </div>
      </div>

      {/* Video Preview Grid */}
      {videos.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {videos.map((video) => (
              <Card key={video.id} className="relative overflow-hidden group">
                <CardContent className="p-2">
                  <div className="relative aspect-9/16 rounded-md overflow-hidden bg-muted">
                    <video
                      src={video.preview}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }}
                    />
                    {video.status !== "pending" && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="text-center text-white space-y-1">
                          {getStatusIcon(video.status)}
                          <p className="text-xs">{getStatusText(video)}</p>
                          {video.status === "uploading" && (
                            <p className="text-xs font-medium">
                              {video.progress}%
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {video.status === "pending" && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="secondary"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCropper(video);
                          }}
                        >
                          <Crop className="h-4 w-4 mr-1" />
                          Crop
                        </Button>
                      </div>
                    )}
                    {!isUploading && video.status === "pending" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeVideo(video.id);
                        }}
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                    {video.cropArea && video.status === "pending" && (
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-emerald-500/90 text-white text-[10px] font-medium">
                        Cropped
                      </div>
                    )}
                  </div>
                  {(video.status === "uploading" ||
                    video.status === "transcoding") && (
                    <Progress value={video.progress} className="mt-2 h-1" />
                  )}
                  {video.error && (
                    <p
                      className="text-xs text-red-500 mt-1 truncate"
                      title={video.error}
                    >
                      {video.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {videos.length} video{videos.length !== 1 ? "s" : ""} selected
            </p>
            <Button
              onClick={startUpload}
              disabled={
                isUploading || videos.every((vid) => vid.status === "complete")
              }
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload All
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Completed Uploads */}
      {/* {completedUploads.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <h3 className="font-semibold">
                Completed Uploads ({completedUploads.length})
              </h3>
            </div>
            <div className="space-y-2 max-h-60 overflow-auto">
              {completedUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center gap-3 p-2 rounded-md bg-muted/50 text-sm"
                >
                  <Video className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{upload.fileName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Blurhash: {upload.thumbnailBlurhash}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-muted-foreground mb-2">
                Result Array (JSON):
              </p>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                {JSON.stringify(completedUploads, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Cropper Dialog */}
      <VideoCropperDialog
        isOpen={!!cropTarget}
        file={cropTarget?.file}
        onClose={() => setCropTarget(null)}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}
